/**
 * The generic `notes` feature — one stream of notes and messages per client,
 * some of them anchored to a visit.
 *
 * The wire is the generated client's: `Note`, `NoteCreate`, `NoteUpdate` and the
 * three enums in `src/api/READ_ONLY/client_api/Api` are the only description of it,
 * and nothing here restates them. What is here is the NORMALISED value the UI reads,
 * which differs from the wire in one deliberate way — see `Note` below.
 */
import type {
  CaregiverNoteFlagEnum,
  Note as NoteWire,
  NoteCreate,
  NoteKindEnum,
  NoteRoleEnum,
  NoteUpdate,
} from '@api/READ_ONLY/client_api/Api';

/**
 * The generated enums as their string unions rather than the enums themselves.
 *
 * TypeScript enums are nominal — two generated modules each declare these, so one
 * module's member is not assignable to the other's — and the union is what both widen
 * to. It is also what the UI and its tests write: a plain `'MESSAGE'`.
 *
 * `NoteFlagValue` is the same union as `NoteFlag` in features/visits/noteFlag; both
 * derive from the generated enum, so values pass freely between them.
 */
export type NoteKind = `${NoteKindEnum}`;
export type NoteRole = `${NoteRoleEnum}`;
export type NoteFlagValue = `${CaregiverNoteFlagEnum}`;

/** Order is ours; membership is checked against the generated enums. */
export const NOTE_KINDS = ['VISIT_NOTE', 'MESSAGE'] as const satisfies readonly NoteKind[];
export const NOTE_ROLES = ['adm', 'cgv', 'doc', 'clt'] as const satisfies readonly NoteRole[];

/**
 * A note as the UI consumes it: a wire row after `normalizeNote` has read it.
 *
 * Derived from the generated `Note`, with the author's role and the audience widened
 * to allow null. The wire says both are always present and always a known code; a
 * response that breaks that promise must not be papered over, because both fields are
 * attribution on clinical content. Unknown stays unknown — see `notes.api.ts`.
 *
 * The rest is `Required` because the normaliser fills every field: what the wire
 * leaves optional it writes as null, so no reader has to handle `undefined` too.
 */
export type Note = Required<Omit<NoteWire, 'kind' | 'authorRole' | 'audience' | 'flag'>> & {
  kind: NoteKind;
  /** Null when the wire carried a role code this app does not know — never guessed. */
  authorRole: NoteRole | null;
  /** Null when the audience could not be read whole; the UI then claims no reach at all. */
  audience: NoteRole[] | null;
  flag: NoteFlagValue | null;
};

/**
 * The filter the panel asks for, in this app's words.
 *
 * Not the generated query object: that one is snake_case, takes `flag` as a repeatable
 * array and carries filters no screen here offers. `listParams` maps this onto it.
 */
export type NoteListQuery = {
  visitId?: number;
  kind?: NoteKind;
  flag?: NoteFlagValue;
  /** One author role, for a panel that shows only what caregivers wrote. */
  authorRole?: NoteRole;
  unreadOnly?: boolean;
  limit?: number;
  offset?: number;
};

/**
 * A page of notes after normalisation — not `PaginatedResponseNote`, whose rows are
 * still wire rows and which carries an offset, a limit and aggregates nothing reads.
 * `total` stays the server's count even when a row was dropped.
 */
export type NoteList = {
  notes: Note[];
  total: number;
};

/**
 * What the composer sends: the generated `NoteCreate`, with both fields it leaves to a
 * server default made explicit. Every note written here names its kind and its
 * audience, and the audience is what keeps an internal remark from reaching a doctor.
 *
 * `authorUserId` and `authorRole` are absent from the generated model on purpose —
 * the INSERT policy pins both from the session.
 */
export type CreateNotePayload = Omit<NoteCreate, 'kind' | 'audience' | 'flag'> & {
  kind: NoteKind;
  audience: NoteRole[];
  flag?: NoteFlagValue | null;
};

/** `body` for the note's author, `flag` for an administrator. Both, for an author who is one. */
export type UpdateNotePayload = Omit<NoteUpdate, 'flag'> & {
  flag?: NoteFlagValue | null;
};
